import type { ReactNode } from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import type {
  SummaryTemplate,
  OutputFormat,
  LengthGuidance,
} from "../../../types/summaryTemplate/SummaryTemplate";

const OUTPUT_FORMAT_KEY: Record<OutputFormat, string> = {
  BulletPoints: "bulletPoints",
  Prose: "prose",
  NumberedList: "numberedList",
};

const LENGTH_KEY: Record<LengthGuidance, string> = {
  Brief: "brief",
  Standard: "standard",
  Detailed: "detailed",
};

// Semantic chip colors: format reads blue, length reads violet — kept
// consistent across template groups so the two facets stay distinguishable.
const FORMAT_CHIP_COLOR = "#9fc2ff";
const LENGTH_CHIP_COLOR = "#cbb6ff";

function FormatIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      style={{ flexShrink: 0 }}
      aria-hidden="true"
    >
      <path
        d="M3 4h2M3 8h2M3 12h2M7 4h6M7 8h6M7 12h6"
        strokeLinecap="round"
      />
    </svg>
  );
}

function LengthIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      style={{ flexShrink: 0 }}
      aria-hidden="true"
    >
      <path
        d="M2 8h12M2 8l2.5-2.5M2 8l2.5 2.5M14 8l-2.5-2.5M14 8l-2.5 2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function MetaChip({
  label,
  color,
  icon,
}: {
  readonly label: string;
  readonly color: string;
  readonly icon: ReactNode;
}) {
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        fontSize: "0.625rem",
        color,
        backgroundColor: `${color}14`,
        border: `1px solid ${color}33`,
        borderRadius: "999px",
        px: 0.75,
        py: 0.25,
        whiteSpace: "nowrap",
      }}
    >
      {icon}
      <Box component="span">{label}</Box>
    </Box>
  );
}

interface TemplateDetailProps {
  readonly template: SummaryTemplate;
  readonly accentColor: string;
}

export default function TemplateDetail({
  template,
  accentColor,
}: TemplateDetailProps) {
  const { t } = useTranslation("summaryTemplate");
  const sortedSections = [...template.sections].sort(
    (a, b) => a.order - b.order,
  );
  const hasGlobal =
    template.globalInstructions != null &&
    template.globalInstructions.trim() !== "";

  return (
    <Box
      sx={{
        mt: 1,
        pt: 1,
        borderTop: "1px solid var(--border-mid)",
        display: "flex",
        flexDirection: "column",
        gap: 1,
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
        {sortedSections.map((section) => (
          <Box key={`${section.order}-${section.heading}`}>
            <Box
              component="span"
              sx={{
                fontSize: "0.75rem",
                fontWeight: 600,
                color: "var(--text-primary)",
                display: "block",
              }}
            >
              {section.heading}
            </Box>
            <Box
              component="span"
              sx={{
                fontSize: "0.6875rem",
                color: "var(--text-tertiary)",
                display: "block",
              }}
            >
              {section.instructions}
            </Box>
          </Box>
        ))}
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
        <MetaChip
          icon={<FormatIcon />}
          label={t(OUTPUT_FORMAT_KEY[template.outputFormat])}
          color={FORMAT_CHIP_COLOR}
        />
        <MetaChip
          icon={<LengthIcon />}
          label={t("lengthChip", {
            value: t(LENGTH_KEY[template.lengthGuidance]),
          })}
          color={LENGTH_CHIP_COLOR}
        />
      </Box>

      {hasGlobal && (
        <Box sx={{ borderLeft: `2px solid ${accentColor}`, pl: 1, py: 0.25 }}>
          <Box
            component="span"
            sx={{
              fontSize: "0.5625rem",
              fontWeight: 700,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: "var(--text-tertiary)",
              display: "block",
              mb: 0.25,
            }}
          >
            {t("globalInstructions")}
          </Box>
          <Box
            component="span"
            sx={{ fontSize: "0.6875rem", color: "var(--text-secondary)" }}
          >
            {template.globalInstructions}
          </Box>
        </Box>
      )}
    </Box>
  );
}
